import {
	IAuFileMetadata,
	IDroppedFile,
	IFileUploadEvent,
	IFileUploadResponse,
} from '@shared/ui-components/au-file-loader/au-file-loader.interfaces';
import { FileUtilities } from '@shared/ui-components/au-file-loader/helpers/file-utilities';
import { FileTypes } from '@shared/ui-components/au-document-type/file-types';
import { signal } from '@angular/core';

export class FileListTools {
	filesList = signal<Partial<IDroppedFile>[]>([]);
	filesFinishedLoading = signal<number>(0);

	resetFilesList() {
		this.filesList.set([]);
		this.filesFinishedLoading.set(0);
	}

	protected pushNewFilesFromChangesToFileList(files: IAuFileMetadata[]) {
		this.filesList.set(
			files
				.map(FileUtilities.makeObjectForExistingFile)
				.sort(FileUtilities.sortFilesByDate)
		);
	}

	protected showDeletionLoader(index: number) {
		this.filesList()[index].deletionLoader?.next(true);
	}

	protected hideDeletionLoader(index: number) {
		this.filesList()[index].deletionLoader?.next(false);
	}

	protected clearAllDeletionLoaders(selections) {
		this.filesList().forEach(fileLink => {
			if (selections.includes(fileLink.pk)) {
				fileLink.deletionLoader.next(false);
			}
		});
	}

	protected removeItemFromFileList(index) {
		this.filesList().splice(index, 1);
		this.filesList.set([...this.filesList()]);
	}

	protected showDeleteLoading(selections: string[]) {
		this.filesList()
			.filter(fileLink => selections.includes(fileLink.pk))
			.forEach(fileLink => fileLink.deletionLoader?.next(true));
	}

	protected handleProgressEvent(data) {
		const fileLink = this.filesList()[data.index];
		fileLink.loadingProgress = FileUtilities.getProgressInPercentage(data);

		if (fileLink.loadingProgress === 100) {
			fileLink.requestBrakeSubject.complete();
			fileLink.file = null;
		}
		this.filesList.set([...this.filesList()]);
	}

	protected handleHttpError(data): void {
		const fileLink = this.filesList()[data.index];
		fileLink.errors.httpError = data.event.message;
		fileLink.requestBrakeSubject.complete();
		fileLink.loadingProgress = 0;
		fileLink.index = data.index;
	}

	protected updateFileInFilesList(response: IFileUploadEvent) {
		const { index, event } = response;

		const updatedFile = this.getUploadedFileData(event, index);
		this.filesList.set([
			...this.filesList().slice(0, index),
			updatedFile,
			...this.filesList().slice(index + 1),
		]);
		this.filesFinishedLoading.set(this.filesFinishedLoading() + 1);
	}

	protected getUploadedFileData(event: any, index: number): IFileUploadResponse {
		if (event.body.errors?.length) {
			return {
				...this.filesList()[index],
				errors: {
					serverError: event.body.errors[0]?.message,
				},
			} as any;
		}

		if (
			!event.body.data.digitalTwinUploadModels?.success &&
			!event.body.data.createFile
		) {
			return {
				...this.filesList()[index],
				errors: {
					serverError: 'Digital Twin model upload failed.',
				},
			} as any;
		}

		const fileData = event.body.data
			? this.getFileDataFromResponse(event.body.data)
			: null;

		let result = {
			...fileData?.file,
			pk: fileData?.pk,
			id: fileData?.id,
			loadingProgress: 100,
			newlyLoaded: true,
			notEmittedYet: true,
			fileMetadata: fileData,
		};

		if (!fileData.file) {
			result = { ...result, name: fileData.name };
		}

		if (fileData.file) {
			result = { ...fileData.file, ...result };
		}

		return result;
	}

	protected get onlyNewlyDownloadedFiles(): any[] {
		return this.filesList()
			.filter(file => file.pk || (file.id && file.notEmittedYet))
			.map(file => file.pk || file.fileMetadata);
	}

	protected get lastFileIndex(): number {
		return this.filesList().length - 1;
	}

	protected hasOldAndMarkedForDeletionFile(selectedFilesIds: string[]): boolean {
		return !!this.filesList().find(file =>
			this.isMarkedForDeletion(file, selectedFilesIds)
		);
	}

	protected getFileTypeName(fileType: string): string {
		return FileTypes[fileType].name;
	}

	private isMarkedForDeletion(
		file: Partial<IDroppedFile>,
		selectedFilesIds: string[]
	): boolean {
		return selectedFilesIds.includes(file.pk) && !file.newlyLoaded;
	}

	private getFileDataFromResponse(response) {
		if (response.createFile) {
			return response.createFile.file;
		} else if (response.digitalTwinUploadModels) {
			/**
			 * For all API endpoints that we use this loader we have different structure of response.
			 * So we need to check all possible responses
			 */

			if (response.digitalTwinUploadModels.uploadModel) {
				return response.digitalTwinUploadModels.uploadModel;
			}

			// uses for digitalTwinUploadModels mutation
			if (response.digitalTwinUploadModels.uploadedModel) {
				return response.digitalTwinUploadModels.uploadedModel;
			}
		}
	}
}
